export type CollabStatus =
  | "offer_pending"
  | "negotiating"
  | "awaiting_payment"
  | "in_progress"
  | "draft_submitted"
  | "revision_requested"
  | "draft_approved"
  | "published"
  | "live_period"
  | "completed"
  | "cancelled"
  | "expired";

export type Party = "creator" | "brand" | "platform" | "none";

export type MilestoneTrigger =
  "on_accept" | "on_draft_approval" | "on_publish" | "on_live_period_end";

/**
 * The only legal edges of the collab lifecycle. Anything not listed here is a bug,
 * and `assertTransition` will refuse it rather than let the collab drift into a
 * state the payment logic was never written for.
 *
 * Payments happen off-platform, so a tranche falling due mid-collab does not move
 * the collab itself — it only flips a milestone to `due` and the pending party to
 * the payer. `awaiting_payment` is used once, for the advance that gates the
 * start of work.
 */
const ALLOWED_TRANSITIONS: Record<CollabStatus, readonly CollabStatus[]> = {
  offer_pending: ["negotiating", "awaiting_payment", "in_progress", "expired", "cancelled"],
  negotiating: ["negotiating", "awaiting_payment", "in_progress", "expired", "cancelled"],
  awaiting_payment: ["in_progress", "expired", "cancelled"],
  // `published` is allowed when the creator submits a final live post and skips
  // the separate draft-review step.
  in_progress: ["draft_submitted", "published", "cancelled"],
  draft_submitted: ["draft_approved", "revision_requested", "cancelled"],
  revision_requested: ["draft_submitted", "published", "cancelled"],
  draft_approved: ["published", "cancelled"],
  // Collaborator can send final work back for changes before approving.
  published: ["live_period", "completed", "revision_requested"],
  live_period: ["completed"],
  completed: [],
  cancelled: [],
  expired: [],
};

/**
 * Whose move it is once a collab reaches a given status. `negotiating` is the one
 * exception — it depends on who sent the last counter — so callers pass it in.
 */
export const PENDING_PARTY_BY_STATUS: Record<CollabStatus, Party> = {
  offer_pending: "creator",
  negotiating: "creator",
  awaiting_payment: "brand",
  in_progress: "creator",
  draft_submitted: "brand",
  revision_requested: "creator",
  draft_approved: "creator",
  published: "brand",
  live_period: "none",
  completed: "none",
  cancelled: "none",
  expired: "none",
};

export function canTransition(from: CollabStatus, to: CollabStatus): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export class IllegalTransitionError extends Error {
  constructor(
    readonly from: CollabStatus,
    readonly to: CollabStatus,
  ) {
    super(`Illegal collab transition: ${from} -> ${to}`);
    this.name = "IllegalTransitionError";
  }
}

export function assertTransition(from: CollabStatus, to: CollabStatus): void {
  if (!canTransition(from, to)) throw new IllegalTransitionError(from, to);
}

export const TERMINAL_STATUSES: readonly CollabStatus[] = ["completed", "cancelled", "expired"];

export function isTerminal(status: CollabStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

/**
 * Basic offers before acceptance — inbox "Other". Stay quiet: no offer push
 * and no chat push until the creator accepts (then they join the main inbox).
 */
export function isQuietBasicOffer(collab: {
  boostTier: string | null | undefined;
  status: string;
}): boolean {
  return (
    (collab.boostTier ?? "basic") === "basic" &&
    (collab.status === "offer_pending" || collab.status === "negotiating")
  );
}

/** A milestone the payer still owes, or that is waiting on the creator's receipt. */
export const OPEN_MILESTONE_STATUSES = ["pending", "due", "paid"] as const;

/**
 * `live_period` only reads as completed once every tranche is settled. Until
 * then the collaborator still owes (or the creator still needs to confirm),
 * so it must not jump into History as "Completed".
 */
export function effectiveCollabStatus(
  status: CollabStatus,
  milestones: { status: string }[],
): CollabStatus {
  if (status === "live_period") {
    const open = milestones.some((m) =>
      OPEN_MILESTONE_STATUSES.includes(m.status as (typeof OPEN_MILESTONE_STATUSES)[number]),
    );
    return open ? "live_period" : "completed";
  }
  return status;
}

/** List rows: same rule — unpaid / unconfirmed money keeps it out of Completed. */
export function effectiveListStatus(
  status: CollabStatus,
  openMilestoneCount: number,
): CollabStatus {
  if (status === "live_period") {
    return openMilestoneCount > 0 ? "live_period" : "completed";
  }
  return status;
}

export function listTimerForStatus(
  status: CollabStatus,
  input: {
    draftDeadline: Date | null;
    paymentDeadline: Date | null;
    liveUntil: Date | null;
  },
  now: Date,
  formatRemaining: (deadline: Date, now: Date) => string | null,
): { timerDeadline: Date | null; timerLabel: string | null } {
  if (isTerminal(status)) {
    return { timerDeadline: null, timerLabel: null };
  }

  let deadline: Date | null = null;
  switch (status) {
    case "offer_pending":
    case "negotiating":
    case "awaiting_payment":
      deadline = input.paymentDeadline;
      break;
    case "in_progress":
    case "revision_requested":
    case "draft_submitted":
    case "draft_approved":
    case "published":
      deadline = input.draftDeadline ?? input.paymentDeadline;
      break;
    default:
      deadline = null;
  }

  if (!deadline) return { timerDeadline: null, timerLabel: null };
  return { timerDeadline: deadline, timerLabel: formatRemaining(deadline, now) };
}

/** Collabs that are under way, where tranches can fall due. */
export const ACTIVE_STATUSES: readonly CollabStatus[] = [
  "in_progress",
  "draft_submitted",
  "revision_requested",
  "draft_approved",
  "published",
  "live_period",
];

/**
 * One-line status text, written from the reader's point of view.
 *
 * `paymentDue` outranks the work state on purpose. A tranche falling due does
 * not move the collab — the creator may still have a post to publish — but from
 * either side's point of view the collab is waiting on money, and saying
 * "Approved — ready to publish" while ₹6,250 sits unpaid buries the thing that
 * actually needs doing.
 */
export function statusLine(
  status: CollabStatus,
  pendingParty: Party,
  viewer: "creator" | "brand",
  paymentDue = false,
  paymentAwaitingConfirm = false,
) {
  const waitingOnYou = pendingParty === viewer;

  // Work review / revision outranks money copy — remaining pay waits on approval.
  if (status === "published") {
    return viewer === "brand"
      ? "Final work needs your approval"
      : "Waiting for work confirmation";
  }
  if (status === "draft_submitted") {
    return viewer === "brand" ? "Work needs your review" : "Work submitted, awaiting review";
  }
  if (status === "revision_requested") {
    return viewer === "creator" ? "Revision requested" : "Waiting on the revised draft";
  }

  // Marked-as-sent outranks an unpaid due — money already moved, confirm next.
  if (paymentAwaitingConfirm && status !== "cancelled" && status !== "expired") {
    return viewer === "creator"
      ? "Confirm payment received"
      : "Waiting for the creator to confirm payment";
  }

  if (paymentDue && status !== "cancelled" && status !== "expired") {
    return viewer === "brand" ? "Payment due — pay the creator" : "Waiting for payment";
  }

  switch (status) {
    case "offer_pending":
      return waitingOnYou ? "New offer needs your response" : "Waiting for the creator to respond";
    case "negotiating":
      return waitingOnYou ? "Counter-offer needs your response" : "Waiting on the other side";
    case "awaiting_payment":
      return viewer === "brand"
        ? "Send the advance to start work"
        : "Waiting for the collaborator to send the advance";
    case "in_progress":
      return viewer === "creator"
        ? "Work under way — draft due"
        : "Creator is working on the draft";
    case "draft_approved":
      return viewer === "creator" ? "Approved — ready to publish" : "Approved, awaiting the post";
    case "live_period":
      return viewer === "creator"
        ? "Post is live — keep it up until the period ends"
        : "Post is live — collab delivered";
    case "completed":
      return "";
    case "cancelled":
      return "Cancelled";
    case "expired":
      return "Offer expired";
  }
}

export type PillTone = "neutral" | "amber" | "blue" | "green" | "red";

export function statusPill(
  status: CollabStatus,
  paymentDue = false,
  paymentAwaitingConfirm = false,
): { label: string; tone: PillTone } {
  // Work under review / revision — don't label the collab as awaiting payment.
  if (
    status === "published" ||
    status === "draft_submitted" ||
    status === "revision_requested"
  ) {
    if (status === "revision_requested") return { label: "In progress", tone: "blue" };
    return { label: "Delivered", tone: "blue" };
  }

  if (paymentAwaitingConfirm && status !== "cancelled" && status !== "expired") {
    return { label: "Awaiting confirmation", tone: "blue" };
  }

  if (paymentDue && status !== "cancelled" && status !== "expired") {
    return { label: "Awaiting payment", tone: "amber" };
  }

  switch (status) {
    // An offer nobody has answered yet is a different thing from one being
    // haggled over: the first is waiting, the second is moving.
    case "offer_pending":
      return { label: "Pending", tone: "amber" };
    case "negotiating":
      return { label: "Negotiating", tone: "amber" };
    case "awaiting_payment":
      return { label: "Awaiting payment", tone: "amber" };
    case "in_progress":
      return { label: "In progress", tone: "blue" };
    case "draft_approved":
      return { label: "Delivered", tone: "blue" };
    case "live_period":
      return { label: "Post live", tone: "green" };
    case "completed":
      return { label: "Completed", tone: "green" };
    case "cancelled":
      return { label: "Cancelled", tone: "neutral" };
    case "expired":
      return { label: "Expired", tone: "neutral" };
  }
}
